import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { updateUiAction } from 'actions/uiActions'
import VideoPanel from 'components/VideoPanel'
import { wallsLocationsSelector } from 'selectors/components'
import getMapProjection from 'helpers/map/getMapProjection'
import snapToWall, { distanceSquared, DOUBLE_CLICK_DISTANCE } from 'helpers/walls/snapToWall'
import { DEFAULT_WALL_OPTIONS, WALL_SNAP_ICON } from 'helpers/globals'
import { addMarkupAction } from 'actions/markupActions'

const mapStateToProps = state => {
  const { map, ui: { drawingWall } } = state

  return {
    map: map.ref,
    drawingWall,
    walls: wallsLocationsSelector(state)
  }
}

class WallInstructions extends React.Component {
  constructor(props) {
    super(props)
    this.mapContainer = null
    this.moveListener = null
    this.checkDoubleClick = null
    this.clickListener = null
    this.downListener = null
    this.skipEventListener = null
    this.dragListener = null
    this.wallListener = null
    this.markups = []
    this.wall = null
    this.completed = false
  }

  componentDidMount = _ => this.activateWallInsertMode()

  componentWillUnmount = _ => this.cancel()

  cancel = removeWall => {
    const { mapContainer, moveListener, checkDoubleClick, clickListener, downListener, skipEventListener,
      dragListener, wallListener, markups, wall, completed } = this

    mapContainer.removeEventListener('mousemove', moveListener)
    mapContainer.removeEventListener('click', checkDoubleClick, true)
    mapContainer.removeEventListener('click', clickListener)
    mapContainer.removeEventListener('mousedown', downListener)
    mapContainer.removeEventListener('touchstart', downListener)
    mapContainer.removeEventListener('rightclick', skipEventListener, true)
    mapContainer.removeEventListener('contextmenu', skipEventListener, true)
    google.maps.event.removeListener(dragListener)
    google.maps.event.removeListener(wallListener)

    markups.forEach(m => m.setMap(null))
    if (!completed) wall.setMap(null)
    this.props.map.setOptions({ draggableCursor: '' })
  }

  activateWallInsertMode = _ => {
    const { map, walls, addMarkupAction, updateUiAction } = this.props

    const mouseFollower = new google.maps.Marker({
      icon: { ...WALL_SNAP_ICON, path: google.maps.SymbolPath.CIRCLE },
      cursor: "url(\"https://maps.google.com/mapfiles/kml/shapes/cross-hairs_highlight.png\") 16 16, crosshair",
      position: map.getCenter(),
      clickable: false,
      zIndex: 100,
      visible: false,
      map
    })

    let path = []
    const wall = new google.maps.Polyline({
      ...DEFAULT_WALL_OPTIONS,
      strokeColor: '#000000',
      path,
      map
    })

    let followerPath = []
    const followerWall = new google.maps.Polyline({
      ...DEFAULT_WALL_OPTIONS,
      strokeColor: '#000000',
      editable: false,
      draggable: false,
      strokeOpacity: 0.55,
      path: followerPath,
      visible: false,
      map
    })

    let followersDisplayed = false
    map.setOptions({ draggableCursor: "url(\"https://maps.google.com/mapfiles/kml/shapes/cross-hairs_highlight.png\") 16 16, crosshair" })

    const mapContainer = document.getElementById('mapContainer')
    const mapComponentRef = mapContainer.children[0]
    const zoomButton = document.querySelector('.gm-bundled-control')
    const projection = getMapProjection(map)

    const complete = _ => {
      this.completed = true
      this.cancel()
      addMarkupAction('wall', wall)
      updateUiAction({ drawingWall: false })
    }

    let moveListenerFrame
    const handleMove = e => {
      const bounds = mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      const snappedPos = snapToWall(relativePixel, projection, walls)

      mouseFollower.setPosition(snappedPos)
      if (path.length > 0) {
        followerPath[1] = snappedPos
        followerWall.setPath(followerPath)
      }

      if (!followersDisplayed) {
        followersDisplayed = true
        mouseFollower.setVisible(true)
        followerWall.setVisible(true)
      }
    }

    this.moveListener = e => {
      if (moveListenerFrame) cancelAnimationFrame(moveListenerFrame)

      moveListenerFrame = requestAnimationFrame(_ => {
        handleMove(e)
        moveListenerFrame = null
      })
    }

    this.checkDoubleClick = e => {
      if (!mapComponentRef.contains(e.target)) return e.stopPropagation()
      if (skipClick || zoomButton && zoomButton.contains(e.target)) return

      const bounds = mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      const snappedPos = snapToWall(relativePixel, projection, walls)

      const pixelPos = projection.fromLatLngToContainerPixel(snappedPos)
      const prevPixelPos = projection.fromLatLngToContainerPixel(path[path.length - 1])

      if (path.length > 0 && Math.sqrt(distanceSquared(pixelPos.x, pixelPos.y, prevPixelPos.x, prevPixelPos.y)) < DOUBLE_CLICK_DISTANCE) {
        e.stopPropagation()
        complete()
      }
    }

    this.clickListener = e => {
      if (skipClick || !mapComponentRef.contains(e.target) || zoomButton && zoomButton.contains(e.target)) return

      const bounds = mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      const snappedPos = snapToWall(relativePixel, projection, walls)

      path.push(snappedPos)
      wall.setPath(path)
      followerPath = [snappedPos, snappedPos]
      followerWall.setPath(followerPath)
      mouseFollower.setPosition(snappedPos)

      if (!followersDisplayed) {
        followersDisplayed = true
        mouseFollower.setVisible(true)
        followerWall.setVisible(true)
      }
    }

    let skipClick = false
    this.downListener = e => {
      if (!mapComponentRef.contains(e.target)) return
      skipClick = false
    }

    this.skipEventListener = e => e.stopPropagation()

    mapContainer.addEventListener('mousemove', this.moveListener)
    mapContainer.addEventListener('click', this.checkDoubleClick, true)
    mapContainer.addEventListener('click', this.clickListener)
    mapContainer.addEventListener('mousedown', this.downListener)
    mapContainer.addEventListener('touchstart', this.downListener)
    mapContainer.addEventListener('rightclick', this.skipEventListener, true)
    mapContainer.addEventListener('contextmenu', this.skipEventListener, true)

    this.wallListener = google.maps.event.addListener(wall, 'click', e => {
      if (e.vertex != 0) return
      path.push(path[0])
      wall.setPath(path)
      complete()
    })

    this.dragListener = google.maps.event.addListener(map, 'dragstart', e => skipClick = true)
    this.markups = [mouseFollower, followerWall]
    this.wall = wall
    this.mapContainer = mapContainer
  }

  render() {
    const { drawingWall, updateUiAction, classes } = this.props

    if (!drawingWall) return null

    return (
      <div className={classes.instructions}>
        <div className={classes.topUiControls}>
          Add Walls
        </div>
        <div className={classes.label}>Walls simulate obstructions of cameras' viewable area.</div>
        <div className={classes.label}>To add walls:</div>
        <ol className={classes.instructionList}>
          <li>Click the wall's first corner, a straight line will extend from there</li>
          <li>Click each corner in the wall as you trace the room</li>
          <li>To complete the wall, double click or click the wall's starting point</li>
        </ol>
        <span className={classes.cancelButton} onClick={_ => {
          updateUiAction({ drawingWall: false })
          this.cancel()
        }}>
          Cancel Drawing
        </span>
        <div className={classes.spacer} />
        <VideoPanel
          videoUrl='https://www.youtube.com/embed/s6QgyW4uqE8?rel=0&showinfo=0'
          uiKey='wallVideoPanelOpen'
        />
      </div>
    )
  }
}

export default connect(mapStateToProps, { updateUiAction, addMarkupAction })(injectSheet(style)(WallInstructions))



// WEBPACK FOOTER //
// ./src/components/WallInstructions/index.js